import React, {
    useEffect,
    useRef,
    useState,
} from 'react';

import {
    Box,
    Image,
    Text,
    Center,
    useColorModeValue,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/react';

import { getFullImageUrl } from '../utils';

export interface BunchBox {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    conf: number;
}

interface ImageWithBoxesProps {
    image: string;
    boxes?: BunchBox[];
    alt?: string;

    /**
     * Ukuran container
     */
    width?: string | number;
    height?: string | number;

    /**
     * Menampilkan confidence score
     */
    showConfidence?: boolean;

    /**
     * Menampilkan jumlah bounding box
     */
    showBoxCount?: boolean;

    /**
     * Warna bounding box
     */
    boxColor?: string;

    /**
     * Ketebalan bounding box
     */
    boxThickness?: number;
}

interface ImageDimensions {
    width: number;
    height: number;
}

interface ImageRenderRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export const ImageWithBoxes: React.FC<ImageWithBoxesProps> = ({
    image,
    boxes = [],
    alt = 'Image',
    width = '100%',
    height = '200px',
    showConfidence = true,
    showBoxCount = true,
    boxColor = '#FF0000',
    boxThickness = 2,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [imageDimensions, setImageDimensions] =
        useState<ImageDimensions>({
            width: 0,
            height: 0,
        });

    const [containerDimensions, setContainerDimensions] =
        useState<ImageDimensions>({
            width: 0,
            height: 0,
        });

    /**
     * Zoom modal
     */
    const [isZoomed, setIsZoomed] = useState(false);

    const backgroundColor = useColorModeValue(
        'gray.100',
        'gray.700'
    );

    const fullImageUrl = getFullImageUrl(image);

    /**
     * =====================================================
     * UPDATE CONTAINER SIZE
     * =====================================================
     */
    const updateContainerSize = () => {
        if (!containerRef.current) {
            return;
        }

        const rect =
            containerRef.current.getBoundingClientRect();

        setContainerDimensions({
            width: rect.width,
            height: rect.height,
        });
    };

    /**
     * =====================================================
     * RESIZE OBSERVER
     * =====================================================
     */
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        updateContainerSize();

        const observer = new ResizeObserver(() => {
            updateContainerSize();
        });

        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    /**
     * =====================================================
     * IMAGE LOAD
     * =====================================================
     */
    const handleImageLoad = (
        event: React.SyntheticEvent<HTMLImageElement>
    ) => {
        const img = event.currentTarget;

        setImageDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
        });

        updateContainerSize();
    };

    /**
     * =====================================================
     * CALCULATE IMAGE RECT
     *
     * Meniru object-fit: contain
     * =====================================================
     */
    const getImageRenderRect = (): ImageRenderRect => {
        if (
            imageDimensions.width <= 0 ||
            imageDimensions.height <= 0 ||
            containerDimensions.width <= 0 ||
            containerDimensions.height <= 0
        ) {
            return {
                left: 0,
                top: 0,
                width: 0,
                height: 0,
            };
        }

        const imageRatio =
            imageDimensions.width /
            imageDimensions.height;

        const containerRatio =
            containerDimensions.width /
            containerDimensions.height;

        let renderedWidth: number;
        let renderedHeight: number;

        if (imageRatio > containerRatio) {
            /**
             * Image lebih lebar
             */
            renderedWidth =
                containerDimensions.width;

            renderedHeight =
                renderedWidth / imageRatio;
        } else {
            /**
             * Image lebih tinggi
             */
            renderedHeight =
                containerDimensions.height;

            renderedWidth =
                renderedHeight * imageRatio;
        }

        return {
            left:
                (containerDimensions.width -
                    renderedWidth) /
                2,

            top:
                (containerDimensions.height -
                    renderedHeight) /
                2,

            width: renderedWidth,
            height: renderedHeight,
        };
    };

    const imageRect =
        getImageRenderRect();

    /**
     * =====================================================
     * RENDER BOUNDING BOXES
     *
     * Dipakai baik di thumbnail maupun modal.
     * =====================================================
     */
    const renderBoundingBoxes = (
        rect: ImageRenderRect
    ) => {
        if (
            rect.width <= 0 ||
            rect.height <= 0 ||
            imageDimensions.width <= 0 ||
            imageDimensions.height <= 0 ||
            boxes.length === 0
        ) {
            return null;
        }

        return (
            <Box
                position="absolute"
                left={`${rect.left}px`}
                top={`${rect.top}px`}
                width={`${rect.width}px`}
                height={`${rect.height}px`}
                pointerEvents="none"
            >
                {boxes.map((box, index) => {
                    /**
                     * Clamp coordinate
                     */
                    const x1 = Math.max(
                        0,
                        Math.min(
                            box.x1,
                            imageDimensions.width
                        )
                    );

                    const y1 = Math.max(
                        0,
                        Math.min(
                            box.y1,
                            imageDimensions.height
                        )
                    );

                    const x2 = Math.max(
                        0,
                        Math.min(
                            box.x2,
                            imageDimensions.width
                        )
                    );

                    const y2 = Math.max(
                        0,
                        Math.min(
                            box.y2,
                            imageDimensions.height
                        )
                    );

                    /**
                     * Pixel -> percentage
                     */
                    const left =
                        (x1 /
                            imageDimensions.width) *
                        100;

                    const top =
                        (y1 /
                            imageDimensions.height) *
                        100;

                    const boxWidth =
                        ((x2 - x1) /
                            imageDimensions.width) *
                        100;

                    const boxHeight =
                        ((y2 - y1) /
                            imageDimensions.height) *
                        100;

                    return (
                        <Box
                            key={`box-${index}`}
                            position="absolute"
                            left={`${left}%`}
                            top={`${top}%`}
                            width={`${boxWidth}%`}
                            height={`${boxHeight}%`}
                            border={`${boxThickness}px solid ${boxColor}`}
                            boxSizing="border-box"
                        >
                            {showConfidence && (
                                <Box
                                    position="absolute"
                                    top={`-${boxThickness + 18}px`}
                                    left={`-${boxThickness}px`}
                                    bg={boxColor}
                                    color="white"
                                    px="5px"
                                    py="2px"
                                    borderRadius="2px"
                                    fontSize="10px"
                                    fontWeight="bold"
                                    lineHeight="normal"
                                    whiteSpace="nowrap"
                                >
                                    #{index + 1}{' '}
                                    {(
                                        box.conf * 100
                                    ).toFixed(1)}
                                    %
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    /**
     * =====================================================
     * EMPTY IMAGE
     * =====================================================
     */
    if (!image) {
        return (
            <Center
                width={width}
                height={height}
                bg={backgroundColor}
                borderRadius="md"
            >
                <Text color="gray.500">
                    Tidak ada gambar
                </Text>
            </Center>
        );
    }

    return (
        <>
            {/* =================================================
                THUMBNAIL
            ================================================== */}
            <Box
                ref={containerRef}
                position="relative"
                width={width}
                height={height}
                bg={backgroundColor}
                overflow="hidden"
                borderRadius="md"
            >
                <Image
                    src={fullImageUrl}
                    alt={alt}
                    width="100%"
                    height="100%"
                    objectFit="contain"
                    display="block"
                    cursor="zoom-in"
                    onLoad={handleImageLoad}
                    onClick={() => setIsZoomed(true)}
                />

                {/* Bounding boxes */}
                {renderBoundingBoxes(imageRect)}

                {/* =================================================
                    AI COUNT
                ================================================== */}
                {/* {showBoxCount &&
                    boxes.length > 0 && (
                        <Box
                            position="absolute"
                            top="10px"
                            right="10px"
                            bg="blackAlpha.700"
                            color="white"
                            px={3}
                            py={1}
                            borderRadius="md"
                            zIndex={5}
                        >
                            <Text
                                fontSize="xs"
                                fontWeight="bold"
                            >
                                AI: {boxes.length} bunch
                            </Text>
                        </Box>
                    )} */}
            </Box>

            {/* =================================================
                FULLSCREEN ZOOM MODAL
            ================================================== */}
            <Modal
                isOpen={isZoomed}
                onClose={() => setIsZoomed(false)}
                size="full"
                isCentered
            >
                <ModalOverlay bg="blackAlpha.900" />

                <ModalContent
                    bg="transparent"
                    boxShadow="none"
                    maxW="100vw"
                    maxH="100vh"
                    m={0}
                >
                    <ModalCloseButton
                        color="white"
                        size="lg"
                        zIndex={20}
                    />

                    <ModalBody
                        p={0}
                        width="100vw"
                        height="100vh"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                        onClick={() =>
                            setIsZoomed(false)
                        }
                    >
                        {/* =================================================
                            IMAGE + BOXES WRAPPER
                            
                            Ukuran wrapper mengikuti ukuran image
                            yang benar-benar tampil.
                        ================================================== */}
                        <Box
                            position="relative"
                            maxW="90vw"
                            maxH="90vh"
                            onClick={(e) =>
                                e.stopPropagation()
                            }
                        >
                            <Image
                                src={fullImageUrl}
                                alt={`Zoomed ${alt}`}
                                maxW="90vw"
                                maxH="90vh"
                                width="auto"
                                height="auto"
                                objectFit="contain"
                                display="block"
                            />

                            {/* 
                                Bounding box pada modal
                                
                                Karena wrapper mengikuti image,
                                kita menggunakan ukuran natural
                                image dengan rasio yang sama.
                            */}
                            {imageDimensions.width > 0 &&
                                imageDimensions.height >
                                    0 && (
                                    <Box
                                        position="absolute"
                                        inset={0}
                                        pointerEvents="none"
                                    >
                                        {boxes.map(
                                            (
                                                box,
                                                index
                                            ) => {
                                                const x1 =
                                                    Math.max(
                                                        0,
                                                        Math.min(
                                                            box.x1,
                                                            imageDimensions.width
                                                        )
                                                    );

                                                const y1 =
                                                    Math.max(
                                                        0,
                                                        Math.min(
                                                            box.y1,
                                                            imageDimensions.height
                                                        )
                                                    );

                                                const x2 =
                                                    Math.max(
                                                        0,
                                                        Math.min(
                                                            box.x2,
                                                            imageDimensions.width
                                                        )
                                                    );

                                                const y2 =
                                                    Math.max(
                                                        0,
                                                        Math.min(
                                                            box.y2,
                                                            imageDimensions.height
                                                        )
                                                    );

                                                const left =
                                                    (x1 /
                                                        imageDimensions.width) *
                                                    100;

                                                const top =
                                                    (y1 /
                                                        imageDimensions.height) *
                                                    100;

                                                const boxWidth =
                                                    ((x2 -
                                                        x1) /
                                                        imageDimensions.width) *
                                                    100;

                                                const boxHeight =
                                                    ((y2 -
                                                        y1) /
                                                        imageDimensions.height) *
                                                    100;

                                                return (
                                                    <Box
                                                        key={`zoom-box-${index}`}
                                                        position="absolute"
                                                        left={`${left}%`}
                                                        top={`${top}%`}
                                                        width={`${boxWidth}%`}
                                                        height={`${boxHeight}%`}
                                                        border={`${boxThickness}px solid ${boxColor}`}
                                                        boxSizing="border-box"
                                                    >
                                                        {showConfidence && (
                                                            <Box
                                                                position="absolute"
                                                                top={`-${boxThickness + 18}px`}
                                                                left={`-${boxThickness}px`}
                                                                bg={boxColor}
                                                                color="white"
                                                                px="5px"
                                                                py="2px"
                                                                borderRadius="2px"
                                                                fontSize="12px"
                                                                fontWeight="bold"
                                                                lineHeight="normal"
                                                                whiteSpace="nowrap"
                                                            >
                                                                #
                                                                {index +
                                                                    1}{' '}
                                                                {(
                                                                    box.conf *
                                                                    100
                                                                ).toFixed(
                                                                    1
                                                                )}
                                                                %
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            }
                                        )}
                                    </Box>
                                )}

                            {/* AI COUNT MODAL */}
                            {/* {showBoxCount &&
                                boxes.length >
                                    0 && (
                                    <Box
                                        position="absolute"
                                        top="10px"
                                        right="10px"
                                        bg="blackAlpha.700"
                                        color="white"
                                        px={3}
                                        py={1}
                                        borderRadius="md"
                                        zIndex={5}
                                    >
                                        <Text
                                            fontSize="sm"
                                            fontWeight="bold"
                                        >
                                            AI:{' '}
                                            {
                                                boxes.length
                                            }{' '}
                                            bunch
                                        </Text>
                                    </Box>
                                )} */}
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default ImageWithBoxes;
